import { useCallback, useRef } from "react";
import { keyboardHeight, keyboardInset, scrollAboveKeyboard } from "@acto/taskpane/field-sales-2/utils/visualViewport";

const KEYBOARD_FIELD_ATTRIBUTE = "data-keyboard-field";
const KEYBOARD_HEIGHT_VAR = "--keyboard-height";

function revealAboveKeyboard(node: HTMLElement | null): boolean {
  const viewport = window.visualViewport;
  if (!viewport || keyboardInset(viewport) === 0) return false;
  scrollAboveKeyboard(node);
  return true;
}

/**
 * Keeps the element that receives `props` inside the visual viewport while the on-screen keyboard
 * is open. Its scroll-margin-top and scroll-margin-bottom apply as `scrollAboveKeyboard` describes.
 *
 * The keyboard finishes opening after focus lands, so the element is revealed again on each visual
 * viewport resize while focus is inside it. Scroll is not followed: following it would fight the
 * user panning the page. `reveal` returns false, and does nothing, while the keyboard is closed.
 *
 * While focus is inside the element and the keyboard is open, `--keyboard-height` on the document
 * root holds the keyboard's height (see `keyboardHeight`). It clears when the keyboard closes or the
 * element unmounts, not on blur, so the layout does not shift between a blur and the tap that
 * caused it.
 */
export function useKeyboardField<T extends HTMLElement>() {
  const nodeRef = useRef<T | null>(null);

  const ref = useCallback((node: T | null) => {
    nodeRef.current = node;
    const viewport = window.visualViewport;
    if (!node || !viewport) return;

    // Focus moving between two elements inside the box fires focusout then focusin, so `focusInside` ends true.
    let focusInside = node.contains(document.activeElement);
    const root = document.documentElement;
    let publishedHeight = false;
    const clearHeight = () => {
      if (publishedHeight) root.style.removeProperty(KEYBOARD_HEIGHT_VAR);
      publishedHeight = false;
    };
    const publishHeight = () => {
      const height = keyboardHeight(viewport);
      if (height === 0) clearHeight();
      else if (focusInside) {
        root.style.setProperty(KEYBOARD_HEIGHT_VAR, `${height}px`);
        publishedHeight = true;
      }
    };
    // A replacement node can take focus while the keyboard stays open, so no resize follows. The
    // node it replaced cleared the height on unmount, and focus-in publishes it again.
    const handleFocusIn = () => {
      focusInside = true;
      publishHeight();
    };
    const handleFocusOut = () => (focusInside = false);
    let frame = 0;
    const handleResize = () => {
      publishHeight();
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (focusInside) revealAboveKeyboard(node);
      });
    };
    node.addEventListener("focusin", handleFocusIn);
    node.addEventListener("focusout", handleFocusOut);
    viewport.addEventListener("resize", handleResize);
    return () => {
      nodeRef.current = null;
      cancelAnimationFrame(frame);
      clearHeight();
      node.removeEventListener("focusin", handleFocusIn);
      node.removeEventListener("focusout", handleFocusOut);
      viewport.removeEventListener("resize", handleResize);
    };
  }, []);

  const reveal = useCallback(() => revealAboveKeyboard(nodeRef.current), []);

  return { props: { ref, [KEYBOARD_FIELD_ATTRIBUTE]: "" }, reveal };
}
